import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress } from "@mui/material";
import { Toaster } from "react-hot-toast";
import LoginPage from "./pages/LoginPage";
import PunchPage from "./pages/PunchPage";
import SettingsPage from "./pages/SettingsPage";
import FaceRegistrationPage from "./pages/FaceRegistrationPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { clearLegacyTokens } from "./services/auth";
import { initSyncQueue } from "./services/syncQueue";
import { BRAND } from "./styles/authTheme";

const theme = createTheme({
  palette: {
    primary: { main: BRAND.orange },
    secondary: { main: BRAND.navy },
    background: { default: BRAND.bg },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  shape: { borderRadius: 10 },
});

function AuthLoadingScreen() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: BRAND.bg,
      }}
    >
      <CircularProgress sx={{ color: BRAND.orange }} />
    </Box>
  );
}

function ProtectedRoute({ children }) {
  const { ready, authenticated } = useAuth();

  if (!ready) {
    return <AuthLoadingScreen />;
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { ready, authenticated } = useAuth();

  if (!ready) {
    return <AuthLoadingScreen />;
  }

  if (authenticated) {
    return <Navigate to="/punch" replace />;
  }

  return children;
}

function AppRoutes() {
  useEffect(() => {
    clearLegacyTokens();
    return initSyncQueue();
  }, []);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/punch"
        element={
          <ProtectedRoute>
            <PunchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/register-face"
        element={
          <ProtectedRoute>
            <FaceRegistrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <Navigate to="/login" replace />
        }
      />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
